import { hmac } from "@noble/hashes/hmac.js";
import { sha256 } from "@noble/hashes/sha2.js";
import { hexToBytes, readUInt32BE } from "./bytes.js";

const REJECT_THRESHOLD = 0xfffffffc;
const encoder = new TextEncoder();

/**
 * Derive a uniformly-distributed 1..6 value from drand randomness via
 * HMAC-SHA256 + rejection sampling.
 *
 * Rejection sampling matters: a naïve `(u32 % 6) + 1` is biased because 2^32
 * is not a multiple of 6 — the last four uint32 values would skew low. We
 * reject any u32 ≥ 0xFFFFFFFC (the largest multiple of 6 below 2^32) and
 * re-derive with a bumped salt until the result lands inside the unbiased
 * window.
 *
 * Inputs are mixed into the HMAC message so identical `(randomness, roomId,
 * counter, dieIdx)` inputs always produce the same output — that's the
 * property the verifier relies on. The same code path runs server-side
 * (BackgammonSession) and in the browser (VerifierModal); using @noble/hashes
 * universally guarantees byte-for-byte parity instead of relying on
 * cross-platform agreement between node:crypto and a JS HMAC port.
 */
export function deriveDie(
  randomnessHex: string,
  roomId: string,
  counter: number,
  dieIdx: number,
): number {
  const key = hexToBytes(randomnessHex);
  for (let salt = 0; salt < 256; salt += 1) {
    const tag = encoder.encode(`${roomId}|${counter}|${dieIdx}|${salt}`);
    const mac = hmac(sha256, key, tag);
    const u32 = readUInt32BE(mac, 0);
    if (u32 < REJECT_THRESHOLD) {
      return (u32 % 6) + 1;
    }
  }
  // Pr ≈ (4/2^32)^256 ≈ 10^-2300. Guard the loop against pathological inputs
  // rather than relying on the probability argument alone.
  throw new Error(
    `deriveDie: rejection sampling failed after 256 attempts (tag prefix=${roomId}|${counter}|${dieIdx})`,
  );
}

/**
 * Derive both dice for a backgammon roll. Counter defaults to 0 because
 * backgammon fetches a fresh drand round per roll — the counter exists so
 * domino (one round per hand, many derived values) can reuse the same
 * primitive, and so future per-round backgammon flows (e.g. doubling cube
 * coin-flips) can mint independent values from the same round.
 */
export function deriveBackgammonDice(
  randomnessHex: string,
  roomId: string,
  counter: number = 0,
): readonly [number, number] {
  return [
    deriveDie(randomnessHex, roomId, counter, 0),
    deriveDie(randomnessHex, roomId, counter, 1),
  ];
}

/**
 * Derive a 31-bit shuffle seed for a domino hand from a single drand round.
 * Feeds the existing `lcgRng()` in `apps/domino/src/game/Deal.ts` — staying
 * inside the 31-bit positive int range so the deal RNG behaves identically
 * to the legacy `Math.floor(Math.random() * 0x7fffffff)` path. The high bit
 * is masked rather than rejection-sampled because the LCG only consumes the
 * low 31 bits — any uniformly distributed u32 modulo 2^31 is also uniform.
 *
 * Counter defaults to 0 (one shuffle per hand today); the parameter exists
 * so future per-hand draws (boneyard picks, hidden-card reveals) can mint
 * independent derived values from the same drand round without colliding
 * with this seed. The literal `"shuffle"` tag stays in the HMAC message so
 * future non-shuffle derivations from the same round are also tag-separated.
 */
export function deriveDominoSeed(
  randomnessHex: string,
  roomId: string,
  counter: number = 0,
): number {
  const key = hexToBytes(randomnessHex);
  const tag = encoder.encode(`${roomId}|${counter}|shuffle`);
  const mac = hmac(sha256, key, tag);
  return readUInt32BE(mac, 0) & 0x7fffffff;
}

/**
 * Derive a wall-shuffle seed for a mahjong hand from a single drand round.
 * Feeds `buildWall(seed)` → `mulberry32` (`apps/mahjong/src/utils/prng.ts`),
 * whose domain is the **full** unsigned 32-bit range (`state = seed >>> 0`) —
 * so unlike `deriveDominoSeed` above there is no 31-bit mask here. Domino masks
 * because its LCG only consumes the low 31 bits; mahjong's PRNG consumes all
 * 32, and masking would throw away a bit the shuffle actually uses.
 * `readUInt32BE` is already uniform over 2^32, so nothing is left to correct.
 *
 * Counter is the hand's index within the room, and it carries more weight here
 * than in domino: `DrandFetcher` caches a round for its ~3 s period, so two
 * hands dealt inside one period would otherwise derive the *same wall*. The
 * literal `"wall"` tag separates this derivation from every other one made from
 * the same round — `deriveDominoSeed`'s `"shuffle"` included, so a room code
 * shared across games can never collide.
 */
export function deriveMahjongSeed(
  randomnessHex: string,
  roomId: string,
  counter: number = 0,
): number {
  const key = hexToBytes(randomnessHex);
  const tag = encoder.encode(`${roomId}|${counter}|wall`);
  const mac = hmac(sha256, key, tag);
  return readUInt32BE(mac, 0);
}
